"use client";

import React from "react";
import { ArrowRight, TrendingUp, Users, CircleCheck, Quote, ChartBar } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const caseStudies = [{
  id: 1,
  client: "Regional Liberal Arts College",
  title: "From Enrollment Cliff to Lifelong Learning Hub",
  challenge: {
    description: "Facing a 15% enrollment decline and an over-reliance on traditional undergraduates, the institution struggled with fragmented continuing education efforts that operated in silos.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1443870/img/campus_grayscale_winter.png",
    stats: ["-15% Enrollment", "Siloed Departments", "High Dependency"]
  },
  solution: {
    description: "We co-designed a centralized lifelong learning ecosystem, embedding new capabilities for adult learner engagement directly into the faculty's workflow.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1443870/img/students_collaborating_teal.png",
    results: ["30% increase in adult learner enrollment within 18 months", "Launched 4 new revenue-generating certificate programs", "Internal team now fully manages the growth engine"]
  },
  quote: "EduStrategy Group didn't just give us a plan; they built the engine with us. We now own our growth strategy.",
  author: "Provost"
}, {
  id: 2,
  client: "State University System",
  title: "Scaling Workforce Development Across 12 Campuses",
  challenge: {
    description: "The system lacked a unified strategy for workforce development, resulting in confused employer partnerships and duplicated administrative costs across campuses.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1443870/img/meeting_room_grayscale.png",
    stats: ["Fragmented Strategy", "Duplicated Costs", "Low Employer Trust"]
  },
  solution: {
    description: "ESG facilitated a system-wide capability building program, establishing a shared framework for employer engagement while retaining local campus autonomy.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1443870/img/modern_university_building_teal.png",
    results: ["Unified employer partnership portal launched", "$2.5M saved in administrative redundancies", "95% retention of corporate partners year-over-year"]
  },
  quote: "The pragmatic, peer-partner approach was exactly what our diverse stakeholders needed to align on a shared vision.",
  author: "Chancellor"
}];
export default function InstitutionalSuccess() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll();
  return <section id="institutional-success" className="bg-background py-16 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-secondary/5 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-primary/5 rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {}
        <div ref={headerRef} className={cn("max-w-3xl mx-auto text-center mb-20 transition-all duration-700 ease-out", headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <h2 className="text-primary font-serif text-4xl md:text-5xl font-bold mb-6">
            Institutional Success
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl leading-relaxed">
            Real transformation happens when strategy is embedded, not just delivered. 
            Explore how we've partnered with institutions to build lasting internal capability.
          </p>
          <div className="mt-8 flex justify-center">
            <Separator className="w-24 bg-secondary h-1 rounded-full" />
          </div>
        </div>

        {}
        <div className="space-y-32">
          {caseStudies.map((study, index) => <CaseStudyRow key={study.id} study={study} index={index} />)}
        </div>

        {}
        <div className="mt-32 text-center">
          <Card className="bg-primary text-primary-foreground max-w-4xl mx-auto overflow-hidden relative border-none shadow-2xl">
            <div className="absolute inset-0 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1443870/img/abstract_geometric_navy.png')] opacity-10 bg-cover bg-center" />
            <CardContent className="relative z-10 py-16 px-8 md:px-16 flex flex-col items-center text-center">
              <h3 className="font-serif text-3xl md:text-4xl font-bold mb-6">
                Ready to Write Your Success Story?
              </h3>
              <p className="text-primary-foreground/80 text-lg mb-8 max-w-2xl">
                Join the forward-thinking institutions that are building sustainable growth engines from within.
              </p>
              <Button asChild size="lg" variant="secondary" className="font-bold tracking-wide uppercase">
                <Link to="https://edustrategygroup.com/schedule-your-strategy-session/">
                  Start a Conversation <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}
function CaseStudyRow({
  study,
  index
}: {
  study: typeof caseStudies[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const isEven = index % 2 === 0;
  return <div ref={ref} data-index={index} className={cn("transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
      {}
      <div className="mb-12 text-center md:text-left md:flex md:items-end md:justify-between border-b border-border pb-6">
        <div>
          <span className="text-secondary font-bold tracking-wider uppercase text-sm mb-2 block">
            {study.client}
          </span>
          <h3 className="text-3xl md:text-4xl font-serif font-bold text-primary">
            {study.title}
          </h3>
        </div>
        <div className="hidden md:block text-muted-foreground font-mono text-sm">
          Case Study 0{study.id}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-stretch">
        
        {}
        <div className={cn("space-y-8", isEven ? "lg:order-1" : "lg:order-2")}>
          <div className="relative group">
            <div className="absolute -inset-4 bg-muted/50 rounded-xl -z-10 transform rotate-1 transition-transform group-hover:rotate-0" />
            <Card className="overflow-hidden border-muted shadow-sm">
              <AspectRatio ratio={16 / 9}>
                <img src={study.challenge.image} alt="Challenge context" className="object-cover w-full h-full grayscale contrast-125 hover:grayscale-0 transition-all duration-700" />
              </AspectRatio>
              <CardHeader className="bg-muted/30 border-b border-border">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <ChartBar className="h-5 w-5" />
                  <span className="font-bold text-sm uppercase tracking-wide">The Challenge (Before)</span>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <p className="text-muted-foreground leading-relaxed mb-6">
                  {study.challenge.description}
                </p>
                <div className="flex flex-wrap gap-3">
                  {study.challenge.stats.map((stat, i) => <span key={i} className="inline-flex items-center px-3 py-1 rounded-full bg-muted text-muted-foreground text-xs font-medium border border-border">
                      {stat}
                    </span>)}
                </div>
              </CardContent>
            </Card>
          </div>

          {}
          <div className="relative py-8 px-6 md:px-10 border-l-4 border-secondary bg-secondary/5 rounded-r-lg">
            <Quote className="absolute top-4 left-4 h-8 w-8 text-secondary/20 -translate-x-1/2 -translate-y-1/2" />
            <blockquote className="text-xl md:text-2xl font-serif italic text-primary leading-relaxed relative z-10">
              "{study.quote}"
            </blockquote>
            <div className="mt-4 flex items-center gap-3">
              <div className="h-px w-8 bg-secondary/50" />
              <cite className="text-sm font-bold text-primary not-italic">
                {study.author}
              </cite>
            </div>
          </div>
        </div>

        {}
        <div className={cn("space-y-8", isEven ? "lg:order-2" : "lg:order-1")}>
          <div className="relative group">
            <div className="absolute -inset-4 bg-secondary/10 rounded-xl -z-10 transform -rotate-1 transition-transform group-hover:rotate-0" />
            <Card className="overflow-hidden border-secondary/20 shadow-lg ring-1 ring-secondary/10">
              <AspectRatio ratio={16 / 9}>
                <img src={study.solution.image} alt="Solution outcome" className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-secondary/10 mix-blend-multiply" />
              </AspectRatio>
              <CardHeader className="bg-secondary/5 border-b border-secondary/10">
                <div className="flex items-center gap-2 text-secondary">
                  <TrendingUp className="h-5 w-5" />
                  <span className="font-bold text-sm uppercase tracking-wide">The Solution (After)</span>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <p className="text-foreground leading-relaxed mb-8 text-lg">
                  {study.solution.description}
                </p>
                
                <div className="space-y-4">
                  <h4 className="font-bold text-primary flex items-center gap-2">
                    <Users className="h-4 w-4 text-secondary" />
                    Key Outcomes
                  </h4>
                  <ul className="space-y-3">
                    {study.solution.results.map((result, i) => <li key={i} className="flex items-start gap-3">
                        <CircleCheck className="h-5 w-5 text-secondary shrink-0 mt-0.5" />
                        <span className="text-foreground/90">{result}</span>
                      </li>)}
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>

          {}
          <div className="flex items-center justify-between p-6 bg-background border border-border rounded-lg shadow-sm">
            <div className="flex flex-col">
              <span className="text-sm text-muted-foreground uppercase tracking-wider font-medium">Transformation Timeline</span>
              <span className="text-2xl font-serif font-bold text-primary">12-18 Months</span>
            </div>
            <Separator orientation="vertical" className="h-10 mx-4" />
            <div className="flex flex-col text-right">
              <span className="text-sm text-muted-foreground uppercase tracking-wider font-medium">Capability Status</span>
              <span className="text-lg font-bold text-secondary flex items-center justify-end gap-2">
                Fully Embedded <CircleCheck className="h-4 w-4" />
              </span>
            </div>
          </div>
        </div>

      </div>
      
      {}
      {index < caseStudies.length - 1 && <div className="my-24 md:my-32">
          <Separator className="bg-border/60" />
        </div>}
    </div>;
}